import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { clearError, updateBookData, updateBook, validateAndSetStep } from '../../../../store/slices/bookSlice';
import { useBookAPI } from '../../../../hooks/useBookAPI';
import PriceBreakdown from './components/PriceBreakdown';
import { useAlert } from '../../../../components/AlertBanner';
import { FEATURES } from '../../../../constants/features';
import { MIN_PAGES, getSuggestedAlternatives } from '../../../../constants/bookFormFactor';
import { trackPricingStepLanded, trackAddonRemoved, trackPricingSwitchAlternative } from '../../../../utils/analytics';

const BASE_PRICE = 0;
const SPELLCHECK_PRICE = 99;
const COVER_DESIGN_PRICE = 2500;
const GST_RATE = 0.09;

const PricingStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const bookData = useAppSelector((state) => state.book.bookData);
  const bookId = useAppSelector((state) => state.book.bookId);
  const { submitForReview, isSaving, error } = useBookAPI();
  const [isRemovingAddon, setIsRemovingAddon] = useState(false);
  const [showCoverWarning, setShowCoverWarning] = useState(false);
  const [pageCountError, setPageCountError] = useState<{ pageCount: number; minPages: number; bindingType: string } | null>(null);

  const minPagesRequired = bookData.bindingType && bookData.paperType
    ? MIN_PAGES[bookData.bindingType]?.[bookData.paperType as keyof (typeof MIN_PAGES)[string]] ?? null
    : null;
  const pagesBelowMin = minPagesRequired !== null && !!bookData.pageCount && bookData.pageCount < minPagesRequired;
  const suggestedAlternatives = pagesBelowMin && bookData.bindingType && bookData.paperType
    ? getSuggestedAlternatives(bookData.pageCount!, bookData.bindingType, bookData.paperType)
    : [];
  // Mirrors the backend's own completeness check (Book.isComplete) — without
  // this, a book with no uploaded file and no written text (e.g. right after
  // deleting its manuscript) still passes every OTHER check here and shows
  // Submit as clickable, only to be rejected by the backend on click.
  const hasManuscriptContent = !!bookData.uploadedFile
    || !!(bookData.content?.html && bookData.content.html.trim().length > 0);

  const spellcheckAdded = FEATURES.SPELLCHECK && (bookData.addons?.spellcheck || false);
  const coverDesignAdded = bookData.addons?.coverDesign || false;
  const hasCoverImage = !!bookData.coverImage;

  const basePrice = BASE_PRICE;
  const spellcheckPrice = spellcheckAdded ? SPELLCHECK_PRICE : 0;
  const coverDesignPrice = coverDesignAdded ? COVER_DESIGN_PRICE : 0;
  const subtotal = basePrice + spellcheckPrice + coverDesignPrice;
  const cgst = Math.round(subtotal * GST_RATE);
  const sgst = Math.round(subtotal * GST_RATE);
  const totalPrice = subtotal + cgst + sgst;
  const isProcessing = isSaving || isRemovingAddon;

  // Fires once on landing so it's a real per-view snapshot of the price shown,
  // not re-fired every time an addon toggle recomputes totalPrice.
  useEffect(() => {
    trackPricingStepLanded({
      total_price: totalPrice,
      spellcheck_added: spellcheckAdded,
      cover_design_added: coverDesignAdded,
      has_cover_image: hasCoverImage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSwitchAlternative = async (bindingType: string, paperType: string) => {
    if (!bookId) return;
    trackPricingSwitchAlternative(bindingType, paperType);
    dispatch(updateBookData({ bindingType, paperType }));
    await dispatch(updateBook({ bookId, bookData: { bindingType, paperType } }));
  };

  const handleRemoveAddon = async (addonType: 'spellcheck' | 'coverDesign') => {
    if (!bookId) return;
    if (addonType === 'coverDesign' && !hasCoverImage) { setShowCoverWarning(true); }
    setIsRemovingAddon(true);
    const updatedAddons = { ...bookData.addons, [addonType]: false };
    try {
      const result = await dispatch(updateBook({ bookId, bookData: { addons: updatedAddons } }));
      if (result.meta.requestStatus === 'fulfilled') {
        dispatch(updateBookData({ addons: updatedAddons }));
        trackAddonRemoved(addonType);
      }
    } finally { setIsRemovingAddon(false); }
  };

  const handleSubmitForReview = async () => {
    if (!bookId) { showAlert('Please save your book first.', 'warning'); return; }
    if (!hasCoverImage && !coverDesignAdded) { setShowCoverWarning(true); return; }
    setPageCountError(null);
    const result = await submitForReview({
      total_price: totalPrice,
      spellcheck_added: spellcheckAdded,
      cover_design_added: coverDesignAdded,
    });
    if (result?.meta?.requestStatus === 'rejected') {
      const data = (result as any)?.payload;
      if (typeof data === 'string' && data.includes('pages')) {
        const match = data.match(/has (\d+) pages?\. (\w+) requires more than (\d+)/);
        if (match) {
          setPageCountError({ pageCount: parseInt(match[1]), minPages: parseInt(match[3]), bindingType: match[2] });
          dispatch(clearError());
        }
      }
    }
  };

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      <div className="hidden md:block mb-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900"><span>←</span><span>Go Back to Dashboard</span></button>
      </div>
      <div className="md:hidden p-4"><button onClick={() => navigate(-1)} className="text-gray-700"><span className="text-xl">←</span></button></div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => dispatch(clearError())} className="text-red-600 text-sm underline ml-4">Dismiss</button>
          </div>
        </div>
      )}

      {showCoverWarning && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-orange-800 text-sm font-medium mb-1">Cannot remove Book Cover Design Package</p>
              <p className="text-orange-700 text-sm">You don't have a cover image. Please upload one or keep the package.</p>
              <button onClick={() => { dispatch(validateAndSetStep('book-cover')); setShowCoverWarning(false); }} className="mt-3 bg-orange-500 text-white px-4 py-2 rounded-md text-sm">Go to Book Cover Step</button>
            </div>
            <button onClick={() => setShowCoverWarning(false)} className="text-orange-600 text-sm underline ml-4">Dismiss</button>
          </div>
        </div>
      )}

      {!hasManuscriptContent && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4 mx-4 md:mx-0">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-red-800 text-sm font-medium">No manuscript content yet</p>
              <p className="text-red-700 text-xs mt-1 leading-relaxed">
                Your book doesn't have an uploaded file or any written content. Please add your manuscript before submitting.
              </p>
              <button
                onClick={() => dispatch(validateAndSetStep('book-formatting'))}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              >
                Go back and add content
              </button>
            </div>
          </div>
        </div>
      )}

      {pagesBelowMin && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4 mx-4 md:mx-0">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
            </svg>
            <div>
              <p className="text-red-800 text-sm font-medium">Not enough pages to submit</p>
              <p className="text-red-700 text-xs mt-1 leading-relaxed">
                Your book has <span className="font-semibold">{bookData.pageCount} pages</span>, but {bookData.bindingType} with {bookData.paperType} requires at least <span className="font-semibold">{minPagesRequired} pages</span>.
                Please add more content before submitting.
              </p>
              {suggestedAlternatives.length > 0 && (
                <div className="mt-3">
                  <p className="text-red-800 text-xs font-medium mb-2">Your {bookData.pageCount} pages qualify for:</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {suggestedAlternatives.map(({ bindingType, paperType }) => (
                      <button
                        key={`${bindingType}-${paperType}`}
                        onClick={() => handleSwitchAlternative(bindingType, paperType)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-red-300 hover:bg-red-50 text-red-700 px-3 py-1.5 rounded-full transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Switch to {bindingType} + {paperType}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => dispatch(validateAndSetStep('book-formatting'))}
                className="mt-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              >
                Go back and add content
              </button>
            </div>
          </div>
        </div>
      )}

      {pageCountError && !pagesBelowMin && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-amber-800 text-sm font-medium mb-1">Not enough pages to submit</p>
              <p className="text-amber-700 text-sm">
                Your book has <span className="font-semibold">{pageCountError.pageCount} pages</span>.{' '}
                {pageCountError.bindingType} requires <span className="font-semibold">more than {pageCountError.minPages} pages</span> to submit.
              </p>
              <button
                onClick={() => { dispatch(validateAndSetStep('book-formatting')); setPageCountError(null); }}
                className="mt-3 bg-amber-500 text-white px-4 py-2 rounded-md text-sm hover:bg-amber-600 transition-colors"
              >
                Go back and add content
              </button>
            </div>
            <button onClick={() => setPageCountError(null)} className="text-amber-600 text-sm underline ml-4 shrink-0">Dismiss</button>
          </div>
        </div>
      )}

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        <div className="space-y-6 max-w-2xl mx-auto">
          <PriceBreakdown
            basePrice={basePrice} spellcheckAdded={spellcheckAdded} coverDesignAdded={coverDesignAdded}
            spellcheckPrice={SPELLCHECK_PRICE} coverDesignPrice={COVER_DESIGN_PRICE}
            cgst={cgst} sgst={sgst} totalPrice={totalPrice}
            isProcessing={isProcessing} isRemovingAddon={isRemovingAddon}
            onRemoveSpellcheck={() => handleRemoveAddon('spellcheck')}
            onRemoveCoverDesign={() => handleRemoveAddon('coverDesign')}
          />

          <button
            onClick={handleSubmitForReview}
            disabled={isProcessing || (!hasCoverImage && !coverDesignAdded) || pagesBelowMin || !hasManuscriptContent}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-lg font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? 'Submitting...' : (
              <>
                Submit for Review
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingStep;
